import { NgModuleRef, StaticProvider } from '@angular/core';
import { downgradeComponent, downgradeInjectable, downgradeModule, setAngularJSGlobal } from '@angular/upgrade/static';

/**
 * Library components
 */

import { PhoneNumberFormatService } from '@onviobr/phone-number-format';
import { ImportStatusComponent } from '@onviobr/toolbox-import-status';
import { ImportWizardComponent } from '@onviobr/toolbox-import-wizard';
import {
	DocumentTreePaneComponent, EsignModalService, ModalCloseService, RecipientSelectorComponent,
	ServiceBootstrapComponent, DocumentApprovalsComponent, ToggleComponent, CommentBoxComponent,
	DocumentGridPaneComponent, DocumentHistoryService, ToastMessageService
} from '@onviobr/document-components';
import { FormElementsComponent, CustomFieldsService, FormElementsService } from '@onviobr/bm-core-custom-fields';
import { BmyecYearEndCloseMainComponent } from '@accounting/year-end-close';
import { QuickBooksIntegrationCallbackComponent, ExternalIntegrationComponent } from '@accounting/external-integration';
import { PeriodsComponent } from '@accounting/periods';
import {
	AtoAgentClientRelationshipComponent, AtoClientFinanceDetailsComponent,
	AuTaxLodgePerformanceComponent,
	AtoClientAssociationDetailsComponent as AUTaxAtoClientAssociationDetailsComponent,
	MigrationDetailsComponent as AUTaxMigrationDetailsComponent,
	SaAtoMigrationModalComponent as AUTaxSaAtoMigrationModalComponent
} from 'autax-staff';
import { FingerprintService } from '@bm-tax/fingerprint';
import { FirmsetupComponent } from '@bm-tax/firmsetup';


/**
 * Local Components
 */
import { ClientCustomFieldsComponent } from './app/custom-fields/custom-fields.component';
import { EmailListComponent } from './app/email-list/email-list.component';
import { BentoComboboxNgComponent } from './app/bento-combobox-ng/bento-combobox-ng.component';
import { BentoAlertNgComponent } from './app/bento-alert-ng/bento-alert-ng.component';
import { WijmoGridNgComponent } from './app/wijmo-grid-ng/wijmo-grid-ng.component';
import { ImportExportTestComponent } from './app/import-export-test/import-export-test.component';
import { SiteSettingsComponent } from './app/site-settings/site-settings.component';
import { ClientDashboardComponent } from './app/client-dashboard/client-dashboard.component';
import { StaffDashboardComponent } from './app/staff-dashboard/staff-dashboard.component';
import { HybridCookieComponent } from './app/hybrid-cookie/hybrid-cookie.component';
import { ImportantMessageComponent } from './app/important-message/important-message.component';
import { FilePreviewComponent } from './app/file-preview/file-preview.component';
import { FirmDashboardComponent } from './app/firm-dashboard/firm-dashboard.component';
import { CostsComponent, AddCostsCalculationTemplateComponent, CalculationTemplateComponent } from '@onviobr/accounting-costs';
import {
	FirmManagementReportMainComponent
} from './app/reports/firm-management-reports/firm-management-report-main/firm-management-report-main.component';
import {
	FirmManagementReportTreeComponent
} from './app/reports/firm-management-reports/firm-management-report-tree/firm-management-report-tree.component';
import {
	FirmManagementReportProfileComponent
} from './app/reports/firm-management-reports/firm-management-report-profile/firm-management-report-profile.component';
import {
	FirmManagementReportOptionComponent
} from './app/reports/firm-management-reports/firm-management-report-option/firm-management-report-option.component';
import { SettingSecurityComponent } from './app/setting-security/setting-security.component';
import { ZoneService } from './app/shared/ZoneService';
import { CustomFieldsSaveService } from './app/custom-fields/custom-fields.service';
import { MigrationSideNavComponent } from './app/migration-side-nav/migration-side-nav.component';
import { MigrationClientRegistrationComponent } from './app/migration-client-registration/migration-client-registration.component';
import { ActivityDetailComponent } from './app/activity-detail/activity-detail.component';
import { DmsViewerService } from './app/file-preview/dms-viewer.service';

import { NotesComponent } from '@onviobr/notes';
import { NotesModalComponent } from '@onviobr/notes';
import { NotificationWrapperComponent } from './app/notification-wrapper/notification-wrapper.component';
import { OpenNotificationWrapperComponent } from './app/open-notification-wrapper/open-notification-wrapper.component';
import { OnvioToast } from '@onviobr/components';
import { OnvioDesignComponentesService } from '@shared';
import { BankingStaffDashboardWrapperComponent } from './app/banking-staff-dashboard-wrapper/banking-staff-dashboard-wrapper.component';
import { NewNotificationWrapperComponent } from './app/new-notification-wrapper/new-notification-wrapper.component';
import { FileMergeComponent } from './app/file-merge/file-merge.component';
import { FileMergeService } from './app/file-merge/file-merge.service';
export function getDowngradedComponents(
	factory: (extra: StaticProvider[]) => Promise<NgModuleRef<unknown>>
): any {
	setAngularJSGlobal(window['angular']);

	return window['angular']
		.module('downgraded', [downgradeModule(factory)])
		.directive(
			'appCustomFields',
			downgradeComponent({
				component: ClientCustomFieldsComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'appActivityDetail',
			downgradeComponent({
				component: ActivityDetailComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'bmcFormElements',
			downgradeComponent({
				component: FormElementsComponent,
				propagateDigest: false,
				inputs: ['uniqueref', 'filters', 'category'],
			})
		)
		.directive(
			'bmcCustomFieldsService',
			downgradeInjectable(CustomFieldsService) as any
		)
		.factory(
			'bmcFormElementsService',
			downgradeInjectable(FormElementsService) as any
		)
		.factory(
			'bmcPhoneNumberFormatService',
			downgradeInjectable(PhoneNumberFormatService) as any
		)
		.factory('zoneService', downgradeInjectable(ZoneService) as any)
		.factory(
			'bdcEsignModalService',
			downgradeInjectable(EsignModalService) as any
		)
		.factory(
			'bdcDocumentHistoryService',
			downgradeInjectable(DocumentHistoryService) as any
		)
		.factory(
			'bdcToastMessageService',
			downgradeInjectable(ToastMessageService) as any
		)
		.factory(
			'bdcModalCloseService',
			downgradeInjectable(ModalCloseService) as any
		)
		.factory(
			'bmcCustomFieldsSaveService',
			downgradeInjectable(CustomFieldsSaveService)
		)
		.factory('bmcDmsViewerService', downgradeInjectable(DmsViewerService))
		.factory('FingerprintService', downgradeInjectable(FingerprintService))

		.factory(
			'appOnvioDesignComponentesService',
			downgradeInjectable(OnvioDesignComponentesService)
		)
		.factory('fileMergeService', downgradeInjectable(FileMergeService))
		.directive(
			'appEmailList',
			downgradeComponent({
				component: EmailListComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'appBentoComboboxNg',
			downgradeComponent({
				component: BentoComboboxNgComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'appBentoAlertNg',
			downgradeComponent({
				component: BentoAlertNgComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'appWijmoGridNg',
			downgradeComponent({
				component: WijmoGridNgComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'appImportExportTest',
			downgradeComponent({
				component: ImportExportTestComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'appSiteSettings',
			downgradeComponent({
				component: SiteSettingsComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'appSettingSecurity',
			downgradeComponent({
				component: SettingSecurityComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'appHybridCookie',
			downgradeComponent({
				component: HybridCookieComponent,
				propagateDigest: false,
				outputs: ['initialized'],
			})
		)
		.directive(
			'appImportantMessage',
			downgradeComponent({
				component: ImportantMessageComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'appClientDash',
			downgradeComponent({
				component: ClientDashboardComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'appFirmDash',
			downgradeComponent({
				component: FirmDashboardComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'appStaffDash',
			downgradeComponent({
				component: StaffDashboardComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'appFilePreview',
			downgradeComponent({
				component: FilePreviewComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'appAccountingCosts',
			downgradeComponent({ component: CostsComponent, propagateDigest: false })
		)
		.directive(
			'setupAccountingCosts',
			downgradeComponent({
				component: CalculationTemplateComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'appAddCostsCalculation',
			downgradeComponent({
				component: AddCostsCalculationTemplateComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'bdcDocumentTreePane',
			downgradeComponent({
				component: DocumentTreePaneComponent,
				inputs: ['selected'],
				outputs: ['selectedChange'],
			})
		)
		.directive(
			'bmyecYearEndCloseMain',
			downgradeComponent({
				component: BmyecYearEndCloseMainComponent,
				propagateDigest: false,
				outputs: ['modalClose', 'modalCancel'],
			})
		)
		.directive(
			'appFirmManagementReportMain',
			downgradeComponent({
				component: FirmManagementReportMainComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'appFirmManagementReportTree',
			downgradeComponent({
				component: FirmManagementReportTreeComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'appFirmManagementReportProfile',
			downgradeComponent({
				component: FirmManagementReportProfileComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'appFirmManagementReportOptionComponent',
			downgradeComponent({
				component: FirmManagementReportOptionComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'autaxAtoAgentClientRelationship',
			downgradeComponent({
				component: AtoAgentClientRelationshipComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'autaxAtoClientFinanceDetails',
			downgradeComponent({
				component: AtoClientFinanceDetailsComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'bmeiQuickbooksCallbackComponent',
			downgradeComponent({
				component: QuickBooksIntegrationCallbackComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'bmeiExternalIntegration',
			downgradeComponent({
				component: ExternalIntegrationComponent,
				propagateDigest: false,
				inputs: ['ownerId', 'showContactDropdown'],
			})
		)
		.directive(
			'bdcRecipientSelector',
			downgradeComponent({
				component: RecipientSelectorComponent,
				inputs: [
					'clientId',
					'companyId',
					'readonly',
					'recipients',
					'maxAllowedCount',
					'showClientCenterCheckbox',
					'recipientValidationContainer',
				],
				outputs: ['recipientsChange'],
			})
		)
		.directive(
			'bdcDocumentApprovals',
			downgradeComponent({
				component: DocumentApprovalsComponent,
				inputs: [
					'clientId',
					'documentApprovals',
					'maxDocumentCount',
					'readonly',
					'disableEsignature',
					'showViewPlaceholdersButton',
				],
				outputs: [
					'addESignPlaceholdersClick',
					'addDocumentClick',
					'removeDocumentClick',
					'esignatureToggleClick',
				],
			})
		)
		.directive(
			'bdcCommentBox',
			downgradeComponent({
				component: CommentBoxComponent,
				inputs: ['entityId'],
			})
		)
		.directive(
			'bdcToggle',
			downgradeComponent({
				component: ToggleComponent,
				inputs: ['toggleModel', 'disabled', 'dataQeIdPrefix'],
				outputs: ['toggleModelChange'],
			})
		)
		.directive(
			'bdcServiceBootstrap',
			downgradeComponent({
				component: ServiceBootstrapComponent,
				outputs: ['initialized'],
			})
		)

		.directive(
			'autaxLodgePerformance',
			downgradeComponent({
				component: AuTaxLodgePerformanceComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'importStatus',
			downgradeComponent({
				component: ImportStatusComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'importWizard',
			downgradeComponent({
				component: ImportWizardComponent,
				inputs: ['type', 'hideTitle'],
				outputs: ['finishImport'],
				propagateDigest: false,
			})
		)
		.directive(
			'autaxAtoClientAssociationDetails',
			downgradeComponent({
				component: AUTaxAtoClientAssociationDetailsComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'autaxMigrationDetails',
			downgradeComponent({
				component: AUTaxMigrationDetailsComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'appSetupNewMigration',
			downgradeComponent({
				component: MigrationSideNavComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'appMigrationClientRegistration',
			downgradeComponent({
				component: MigrationClientRegistrationComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'bdcDocumentGridPane',
			downgradeComponent({
				component: DocumentGridPaneComponent,
			})
		)
		.directive(
			'autaxSaAtoMigrationModal',
			downgradeComponent({
				component: AUTaxSaAtoMigrationModalComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'bmppPeriods',
			downgradeComponent({
				component: PeriodsComponent,
				propagateDigest: false,
				inputs: ['ownerId', 'showRelatedContacts', 'isFirmClient'],
			})
		)

		.directive(
			'ngBluemoonUstaxFirmsetup',
			downgradeComponent({
				component: FirmsetupComponent,
				propagateDigest: false,
			})
		)
		.directive(
			'bmcnNotes',
			downgradeComponent({
				component: NotesComponent,
				inputs: ['entityId', 'entityName', 'commentCount', 'noteType', 'icon', 'isLocked']
			})
		)
		.directive(
			'bmcnNotesModal',
			downgradeComponent({
				component: NotesModalComponent,
				inputs: ['entityId','icon','name','noteType','qeIdPrefix','userContactId', 'isNotesModal']
			})
		).directive(
			'notificationWrapper',
			downgradeComponent({
				component: NotificationWrapperComponent,
				propagateDigest: false,
			})
		).directive(
			'openNotificationWrapper',
			downgradeComponent({
				component: OpenNotificationWrapperComponent,
				propagateDigest: false,
			})
		).directive(
			'bankingStaffDashboardWrapper',
			downgradeComponent({
				component: BankingStaffDashboardWrapperComponent,
				propagateDigest: false,
			})
		).directive(
			'onvioToast',
			downgradeComponent({
				component: OnvioToast,
				inputs: ['type', 'headerText', 'bodyText'],
				outputs: ['toastClosed']
			})
		).directive(
			'appNewNotifications',
			downgradeComponent({
				component: NewNotificationWrapperComponent,
				propagateDigest: false,
			})
		).directive(
			'fileMerger',
			downgradeComponent({
				component: FileMergeComponent,
				propagateDigest: false,
			})
		);
}
